/**
 * Wallet - 3D Wallet Model with Bills
 *
 * Features:
 * - GLTF model loading based on quality setting
 * - Bills visible sticking out (showing current denomination)
 * - Two animations: enter (off-screen → closed), open/close (closed ↔ open)
 * - Denomination cycling
 * - Bill extraction and return
 */

import * as THREE from 'three';
import gsap from 'gsap';
import { AssetLoader, type AssetQuality } from '../../engine/loaders/AssetLoader';
import { Bill } from './Bill';
import { type Denomination, getDenomination, getDenominations } from './BillDenominations';
import { WalletAnims } from '../config/AnimationConfig';
import { sound, SoundId } from '../../engine/audio/sound';
import { renderScheduler } from '../../engine/render/RenderScheduler';
import { WalletInstructionsLabel } from '../../ui/components/WalletInstructionsLabel';

export interface WalletConfig {
  assetLoader: AssetLoader;
  scene: THREE.Scene;
  quality?: AssetQuality;
  position?: { x: number; y: number; z: number };
  billSegments?: number;
  billShadows?: boolean;
  billTextureSize?: number;
}

/**
 * Three terminal states the wallet can sit in. The async show/hide API
 * declares a target; the wallet figures out the minimum animation chain
 * needed to reach it from the current state.
 *   - 'hidden'  — masterGroup invisible (off-screen).
 *   - 'closed'  — masterGroup visible, billfold shut (openProgress 0).
 *   - 'open'    — masterGroup visible, billfold open (openProgress 1).
 */
export type WalletState = 'hidden' | 'closed' | 'open';

// Persist the player's last-picked denomination index across sessions so the
// wallet reopens on the same note. Shared by every mode (craps/cee-lo/solo)
// because each mode builds its own Wallet but reads the same key.
const WALLET_DENOM_INDEX_KEY = 'sdl.wallet.denomIndex';

const DEFAULT_POSITION = { x: 2, y: 1.8, z: 3 };
const WALLET_MODEL_SCALE = 0.22;
const WALLET_MODEL_Z_OFFSET = 0.2;
const WALLET_MODEL_ROTATION_Z = -0.05;
const WALLET_MASTER_ROTATION_Y = -Math.PI / 6;
const BILL_EXTRACT_HEIGHT_OFFSET = 1.1;
const BILL_EXTRACT_Z_OFFSET = -1.0;
const BILL_RETURN_X_OFFSET = -0.3;
const BILL_RETURN_Y_OFFSET = 0.5;
const BILL_RETURN_Z_OFFSET = 0.2;
const SELECTED_BILL_TILT_Z = Math.PI / 25;
const SELECTED_BILL_RAISE_Y = 0.5;
const SELECTED_BILL_FORWARD_X = 0.15;

export class Wallet {
  private assetLoader: AssetLoader;
  private scene: THREE.Scene;
  private billSegments: number;
  private billShadows: boolean;
  private billTextureSize: number;

  // Master group containing wallet + bills
  private masterGroup: THREE.Group = new THREE.Group();

  // Wallet model
  private walletGroup: THREE.Group | null = null;
  private loaded = false;

  // Bills in wallet (visible bills sticking out)
  private visibleBills: Bill[] = [];
  private visibleBillsGroup: THREE.Group = new THREE.Group(); // Group for all wallet bills
  private visibleDenominations: Denomination[] = [];
  private currentDenominationIndex = 0;
  /**
   * Spendable amount this round (table stack − committed bets − pulled stack).
   * `null` = no cap yet (show every denomination). Notes worth more than this
   * are hidden from the fan so the player can never pull more than they have.
   */
  private availableBalance: number | null = null;
  /** Last affordable-ceiling index applied, to skip redundant fan rebuilds. */
  private lastAffordableMaxIndex = Number.NaN;

  // Animation state
  private isOpen = false;
  private openProgress = 0;
  /** HTML tutorial label anchored to wallet open/close lifecycle. */
  private instructionsLabel: WalletInstructionsLabel | null = null;
  private openCloseTween: gsap.core.Tween | null = null;

  // Entry animation — slide-in from off-screen to closed position.
  // openProgress -0.3 → 0 (below viewport → closedPosition)
  private animatingEntry = false;

  // Async target-state tracking. State classes call show({open, animated})
  // or hide({animated}); the wallet computes the minimum animation chain to
  // reach the target from its current state. Idempotent: a call whose target
  // matches the current target (with no animation in flight) resolves
  // immediately. A new target overrides any in-flight transition (the old
  // promise is resolved, not rejected, so awaiters complete cleanly).
  private targetState: WalletState = 'hidden';
  private inflightTransition: Promise<void> | null = null;
  // Set by completeEntryNow() to fast-forward an in-flight entrance chain.
  // Each step of runTransition checks it and finishes its tween immediately.
  private entryTweenSkip = false;
  // LIVENESS: resolver for the async step currently awaiting openCloseTween's
  // onComplete. Every kill of that tween MUST go through killOpenCloseTween so
  // this gets released — an orphaned resolver suspends the awaiting hideWallet/
  // showWallet forever, which jams the game-update TaskPool and freezes the
  // client on the previous phase (the cee-lo stuck-in-betting incident).
  private pendingStepResolve: (() => void) | null = null;
  // Monotonic id of the active transition chain. A chain that resumes from a
  // released await and sees a newer id has been superseded — it stops instead
  // of zombie-running its remaining steps over the new owner's work.
  private chainId = 0;

  // Selection mode (fan out bills for denomination selection)
  private isSelectionMode = false;
  private selectionModeProgress = 0;
  private selectionTween: gsap.core.Tween | null = null;
  private billRestPositions: { x?: number; y: number; z: number; rotY: number; rotZ?: number }[] = [];
  private billBasePositions: { x: number; y: number; z: number; rotY: number; rotZ: number }[] = [];

  // Position
  private position: THREE.Vector3;
  private closedPosition: THREE.Vector3 | null = null;
  private openPosition: THREE.Vector3 | null = null;
  private closedRotationY = 0;
  private closedRotationX = 0;
  private closedRotationZ = 0;
  private openRotationY = WALLET_MASTER_ROTATION_Y;
  private openRotationX = 0;
  private openRotationZ = 0;

  constructor(config: WalletConfig) {
    this.assetLoader = config.assetLoader;
    this.scene = config.scene;
    this.billSegments = config.billSegments ?? 8;
    this.billShadows = config.billShadows ?? true;
    this.billTextureSize = config.billTextureSize ?? 1024;

    this.position = new THREE.Vector3(
      config.position?.x ?? DEFAULT_POSITION.x,
      config.position?.y ?? DEFAULT_POSITION.y,
      config.position?.z ?? DEFAULT_POSITION.z
    );

    // Restore the last-used denomination so the wallet opens where it was left.
    // Bounds are only sanity-checked here (>= 0); the upper bound is handled
    // lazily by the affordability clamp + `denoms[i] ?? denoms[0]` fallbacks,
    // since the denomination list may not be loaded from the server yet.
    this.currentDenominationIndex = this.loadPersistedDenominationIndex();

    // Hidden by default; shown explicitly when betting is active.
    this.masterGroup.visible = false;

    // Tutorial label lives in the DOM overlay, but its lifecycle is owned
    // by the wallet — show at open-complete, hide at close-start / hide().
    const uiContainer = document.getElementById('ui-container');
    if (uiContainer) {
      this.instructionsLabel = new WalletInstructionsLabel({ container: uiContainer });
    }
  }

  /** Access the tutorial label so external code can mark gesture flags. */
  getInstructionsLabel(): WalletInstructionsLabel | null {
    return this.instructionsLabel;
  }

  /** Read the persisted denomination index, or 0 if unset/invalid. */
  private loadPersistedDenominationIndex(): number {
    try {
      const raw = localStorage.getItem(WALLET_DENOM_INDEX_KEY);
      if (raw == null) return 0;
      const parsed = parseInt(raw, 10);
      return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    } catch {
      return 0;
    }
  }

  /** Save the current denomination index so the next session restores it. */
  private persistDenominationIndex(): void {
    try {
      localStorage.setItem(WALLET_DENOM_INDEX_KEY, String(this.currentDenominationIndex));
    } catch {
      // localStorage can throw (private mode / quota) — persistence is best-effort.
    }
  }

  /**
   * Load wallet model (GLB with baked textures, OBJ fallback)
   */
  async load(): Promise<void> {
    try {
      await this.loadGLB();
      this.loaded = true;
    } catch (error) {
      console.warn('Failed to load wallet GLB, trying OBJ fallback:', error);
      try {
        await this.loadOBJ();
        this.loaded = true;
      } catch (objError) {
        console.error('Failed to load wallet OBJ, falling back to simple geometry:', objError);
        this.createSimpleWallet();
        this.loaded = true;
      }
    }
  }

  /**
   * Manually apply textures to the wallet materials
   */
  private async applyTexturesManually(obj: THREE.Group): Promise<void> {
    const basePath = 'models/wallet/';

    // Define material configs
    const materialConfigs: Record<string, { color: number; roughness: number; metalness: number }> = {
      leather_Mat: { color: 0x4a3426, roughness: 0.85, metalness: 0.0 },
      zipper_Mat: { color: 0xaaaaaa, roughness: 0.3, metalness: 0.9 },
      paper_Mat: { color: 0xf0f0e8, roughness: 0.9, metalness: 0.0 },
      plastic_Mat: { color: 0x333333, roughness: 0.4, metalness: 0.1 },
    };

    // Load textures for each material
    for (const [materialName, config] of Object.entries(materialConfigs)) {
      try {
        const diffusePath = `${basePath}walletforman02wb_${materialName}_Diffuse.jpg`;
        const normalPath = `${basePath}walletforman02wb_${materialName}_Normal.jpg`;


        // Load textures
        const [diffuseMap, normalMap] = await Promise.all([
          this.assetLoader.loadTexture(diffusePath).catch(() => null),
          this.assetLoader.loadTexture(normalPath).catch(() => null),
        ]);

        // Apply to meshes with this material
        obj.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            const mat = child.material as THREE.Material;
            if (mat.name === materialName) {

              child.material = new THREE.MeshStandardMaterial({
                map: diffuseMap,
                normalMap: normalMap,
                color: config.color,
                roughness: config.roughness,
                metalness: config.metalness,
              });
            }
          }
        });
      } catch (error) {
        console.warn(`⚠️ Failed to load textures for ${materialName}`, error);
      }
    }
  }

  /**
   * Load wallet GLB model (textures baked in, nodes already scaled to 0.01)
   */
  private async loadGLB(): Promise<void> {
    const gltf = await this.assetLoader.loadGLTF('models/wallet/wallet.glb');
    const model = gltf.scene;

    this.walletGroup = new THREE.Group();
    this.walletGroup.add(model);

    // GLB nodes already have scale 0.01 baked in — apply a scene-level multiplier
    // to match the visual size the OBJ had at WALLET_MODEL_SCALE (0.22).
    // OBJ raw was ~100× bigger, so OBJ_SCALE(0.22) ≈ GLB_NODE_SCALE(0.01) × 22.
    const GLB_SCENE_SCALE = 22;
    model.scale.set(GLB_SCENE_SCALE, GLB_SCENE_SCALE, GLB_SCENE_SCALE);
    model.position.z = WALLET_MODEL_Z_OFFSET;
    model.rotation.x = -Math.PI / 2;
    model.rotation.z = WALLET_MODEL_ROTATION_Z;

    model.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
        child.receiveShadow = false;
      }
    });

    this.masterGroup.add(this.walletGroup);
    this.masterGroup.add(this.visibleBillsGroup);

    this.masterGroup.position.copy(this.position);
    this.masterGroup.rotation.y = WALLET_MASTER_ROTATION_Y;

    this.scene.add(this.masterGroup);
    this.createVisibleBills();

  }

  /**
   * Load wallet OBJ model (legacy fallback)
   */
  async loadOBJ(): Promise<void> {
    // Load OBJ model with MTL materials
    const obj = await this.assetLoader.loadOBJ('models/wallet/wallet.obj', 'models/wallet/wallet.mtl');

    this.walletGroup = new THREE.Group();
    this.walletGroup.add(obj);

    // Scale down the wallet (OBJ models are often exported in different units)
    // Adjust this scale to fit properly with our scene
    obj.scale.set(WALLET_MODEL_SCALE, WALLET_MODEL_SCALE, WALLET_MODEL_SCALE);
    obj.position.z = WALLET_MODEL_Z_OFFSET
    //obj.position.y += 0.2

    // Rotate wallet to lay flat on table (it's currently standing up)
    obj.rotation.x = -Math.PI / 2; // Rotate 90 degrees to lay flat
    obj.rotation.z = WALLET_MODEL_ROTATION_Z

    // Manually load and apply textures for each material
    await this.applyTexturesManually(obj);

    obj.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });

    // Add wallet and bills groups to master group
    this.masterGroup.add(this.walletGroup);
    this.masterGroup.add(this.visibleBillsGroup);

    // Position and rotate the MASTER group once
    this.masterGroup.position.copy(this.position);
    this.masterGroup.rotation.y = WALLET_MASTER_ROTATION_Y; // Angled toward camera

    this.scene.add(this.masterGroup);

    // Create initial visible bills
    this.createVisibleBills();

  }

  /**
   * Create simple wallet using basic box geometry (for testing)
   */
  private createSimpleWallet(): void {
    this.walletGroup = new THREE.Group();

    // Main wallet body (dark brown leather look) - BIGGER
    const bodyGeometry = new THREE.BoxGeometry(2.0, 1.2, 0.4);
    const bodyMaterial = new THREE.MeshStandardMaterial({
      color: 0x3d2817, // Slightly lighter brown for visibility
      roughness: 0.8,
      metalness: 0.1,
    });
    const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    body.castShadow = true;
    body.receiveShadow = true;

    // Add a gold trim/accent line - BIGGER
    const trimGeometry = new THREE.BoxGeometry(2.1, 0.1, 0.45);
    const trimMaterial = new THREE.MeshStandardMaterial({
      color: 0xFFD700, // Gold
      roughness: 0.3,
      metalness: 0.7,
    });
    const trim = new THREE.Mesh(trimGeometry, trimMaterial);
    trim.position.y = 0.65;

    // Add bright indicator so it's easy to see
    const indicatorGeometry = new THREE.BoxGeometry(0.3, 0.3, 0.5);
    const indicatorMaterial = new THREE.MeshStandardMaterial({
      color: 0xFF6600, // Bright orange
      emissive: 0xFF3300,
      emissiveIntensity: 0.5,
    });
    const indicator = new THREE.Mesh(indicatorGeometry, indicatorMaterial);
    indicator.position.set(0, 0.8, 0);

    this.walletGroup.add(body);
    this.walletGroup.add(trim);
    this.walletGroup.add(indicator);
    // Wallet at local origin (no position/rotation here)

    // Add wallet and bills groups to master group
    this.masterGroup.add(this.walletGroup);
    this.masterGroup.add(this.visibleBillsGroup);

    // Position and rotate the MASTER group once
    this.masterGroup.position.copy(this.position);
    this.masterGroup.rotation.y = WALLET_MASTER_ROTATION_Y; // Angled toward camera

    this.scene.add(this.masterGroup);

    this.createVisibleBills();
  }

  /**
   * Create bills visible sticking out of wallet (fanned out like cards)
   * Shows multiple denominations - tap/swipe to cycle through them
   */
  private createVisibleBills(options: { startAtRest?: boolean } = {}): void {
    const { startAtRest = false } = options;
    // Clear existing visible bills from group
    for (const bill of this.visibleBills) {
      this.visibleBillsGroup.remove(bill.getMesh());
      bill.dispose();
    }
    this.visibleBills = [];
    this.billRestPositions = [];
    this.billBasePositions = [];


    // Show denominations stacked like cards on top of wallet
    const denomsToShow = this.getVisibleDenominations();
    this.visibleDenominations = denomsToShow;

    for (let i = 0; i < denomsToShow.length; i++) {
      const denomination = denomsToShow[i];
      const isCurrentDenom = denomination === this.getSelectedDenomination();

      // Base positions
      const baseY = 0.3;
      const baseZ = i * -0.02;
      const baseRotY = i * 0.02;

      // Selected bill tilts sideways and moves forward to pop out from stack
      const selectedTiltZ = SELECTED_BILL_TILT_Z;
      const selectedRaiseY = SELECTED_BILL_RAISE_Y;
      const selectedForwardX = SELECTED_BILL_FORWARD_X;

      const bill = Bill.acquire({
        denomination,
        segments: this.billSegments,
        shadows: this.billShadows,
        textureSize: this.billTextureSize,
        assetLoader: this.assetLoader,
        position: {
          x: startAtRest ? 0 : isCurrentDenom ? selectedForwardX : 0,
          y: startAtRest ? baseY : isCurrentDenom ? baseY + selectedRaiseY : baseY,
          z: baseZ,
        },
      });

      this.billBasePositions.push({
        x: 0,
        y: baseY,
        z: baseZ,
        rotY: baseRotY,
        rotZ: 0,
      });

      // Store rest position for animation
      this.billRestPositions.push({
        x: isCurrentDenom ? selectedForwardX : 0,
        y: isCurrentDenom ? baseY + selectedRaiseY : baseY,
        z: baseZ,
        rotY: baseRotY,
        rotZ: isCurrentDenom ? selectedTiltZ : 0,
      });

      // Rotate bills to fan toward center from left side (match wallet triangular closing)
      // Selected bill gets additional sideways tilt to pop out
      bill.setRotation({
        x: 0,
        y: baseRotY,
        z: startAtRest ? 0 : isCurrentDenom ? selectedTiltZ : 0,
      });
      bill.setBend(0);

      this.applyBillHighlight(bill, isCurrentDenom && !startAtRest);

      // Add to group instead of scene
      this.visibleBillsGroup.add(bill.getMesh());
      this.visibleBills.push(bill);
    }
  }

  /**
   * Enter selection mode - fan out bills for denomination picking
   */
  enterSelectionMode(): void {
    if (this.isSelectionMode) return;
    this.isSelectionMode = true;
    this.tweenSelectionMode(1);
  }

  /**
   * Exit selection mode - collapse bills back
   */
  exitSelectionMode(): void {
    if (!this.isSelectionMode) return;
    this.isSelectionMode = false;
    this.tweenSelectionMode(0);
  }

  private tweenSelectionMode(target: number): void {
    if (this.selectionTween) { this.selectionTween.kill(); this.selectionTween = null; }
    this.selectionTween = gsap.to(this, {
      selectionModeProgress: target,
      duration: WalletAnims.selectionFanDuration,
      ease: WalletAnims.selectionFanEase,
      onUpdate: () => { this.applySelectionFan(); },
      onComplete: () => { this.selectionTween = null; },
    });
  }

  /**
   * Check if in selection mode
   */
  getIsSelectionMode(): boolean {
    return this.isSelectionMode;
  }

  /**
   * Select denomination by bill index (when tapping a fanned bill)
   */
  selectDenominationByIndex(billIndex: number): void {
    const denomsToShow =
      this.visibleDenominations.length > 0 ? this.visibleDenominations : this.getVisibleDenominations();
    if (billIndex < 0 || billIndex >= denomsToShow.length) return;

    const denomination = denomsToShow[billIndex];

    // Find the denomination index in the main DENOMINATIONS array
    const denomIndex = getDenominations().indexOf(denomination);
    if (denomIndex !== -1) {
      this.currentDenominationIndex = denomIndex;
      this.persistDenominationIndex();
    }

    // Exit selection mode and rebuild bills
    this.exitSelectionMode();

    // Delay rebuild to let collapse animation play
    setTimeout(() => {
      this.createVisibleBills();
    }, WalletAnims.rebuildDelay);
  }

  /**
   * Get bill index from mesh (for raycasting)
   */
  getBillIndexFromMesh(mesh: THREE.Mesh): number {
    for (let i = 0; i < this.visibleBills.length; i++) {
      if (this.visibleBills[i].getMesh() === mesh) {
        return i;
      }
    }
    return -1;
  }

  /**
   * Set current denomination index
   */
  setDenomination(index: number): void;
  setDenomination(index: number, options: { animate?: boolean }): void;
  setDenomination(index: number, options: { animate?: boolean } = {}): void {
    const denoms = getDenominations();
    if (index < 0 || index >= denoms.length) {
      console.warn('Invalid denomination index:', index);
      return;
    }
    // Never select a note the player can't afford (radial menu shows the full
    // ladder); clamp down to the largest affordable one.
    const maxAffordable = this.affordableDenominations().length - 1;
    if (maxAffordable >= 0 && index > maxAffordable) index = maxAffordable;

    const shouldAnimate = options.animate ?? false;
    // Note-slide click only on a real, user-driven change (animated select/swap).
    // Programmatic, non-animated sets (betting entry, tutorial init, affordability
    // clamp) stay silent.
    if (shouldAnimate && index !== this.currentDenominationIndex) {
      sound.play(SoundId.NOTE_SLIDE);
    }
    const nextVisibleDenoms = shouldAnimate
      ? this.getVisibleDenominationsForIndex(index)
      : [];
    const reuseVisibleBills =
      shouldAnimate &&
      this.visibleBills.length > 0 &&
      this.areDenominationSetsEqual(this.visibleDenominations, nextVisibleDenoms);

    this.currentDenominationIndex = index;
    this.persistDenominationIndex();

    if (shouldAnimate) {
      this.isSelectionMode = false;
      if (this.selectionTween) { this.selectionTween.kill(); this.selectionTween = null; }
      this.selectionModeProgress = 0;

      if (reuseVisibleBills) {
        this.visibleDenominations = nextVisibleDenoms;
        this.recalculateBillPositions();
        this.animateDenominationSelection();
        return;
      }

      this.createVisibleBills({ startAtRest: true });
      this.animateDenominationSelection();
      return;
    }

    this.createVisibleBills();
  }

  /**
   * Cycle denomination up or down
   */
  cycleDenomination(direction: 'up' | 'down'): void {
    const len = this.affordableDenominations().length;
    if (len === 0) return;
    if (direction === 'up') {
      this.currentDenominationIndex = (this.currentDenominationIndex + 1) % len;
    } else {
      this.currentDenominationIndex = (this.currentDenominationIndex - 1 + len) % len;
    }
    this.persistDenominationIndex();
    sound.play(SoundId.NOTE_SLIDE);

    this.createVisibleBills();
  }

  /**
   * Get visible denominations in wallet stack
   */
  private getVisibleDenominations(): Denomination[] {
    return this.getVisibleDenominationsForIndex(this.currentDenominationIndex);
  }

  private getVisibleDenominationsForIndex(index: number): Denomination[] {
    const denoms = this.affordableDenominations();
    const visibleCount = 8;
    const total = denoms.length;
    if (total === 0) return []; // nothing affordable → empty wallet
    const clampedCount = Math.min(visibleCount, total);

    if (clampedCount === total) {
      return [...denoms];
    }

    const half = Math.floor(clampedCount / 2);
    const maxStart = total - clampedCount;
    // `index` is a full-list index; the affordable list is a prefix of it, so
    // clamp into range before windowing.
    const localIndex = Math.min(Math.max(index, 0), total - 1);
    const start = Math.max(0, Math.min(localIndex - half, maxStart));

    return denoms.slice(start, start + clampedCount);
  }

  /**
   * Denominations the player can currently afford. The denomination list is
   * sorted ascending, so the affordable set is always a prefix — everything
   * worth more than `availableBalance` is dropped.
   */
  private affordableDenominations(): Denomination[] {
    const denoms = getDenominations();
    if (this.availableBalance == null) return denoms;
    const cap = this.availableBalance;
    return denoms.filter((d) => d <= cap);
  }

  /**
   * Seed the spendable amount so the wallet hides notes the player can't cover
   * and, if the selected note becomes unaffordable, drops the selection to the
   * largest affordable one. Cheap to call repeatedly: only rebuilds the fan
   * when the affordable ceiling actually moved or the selection had to change.
   */
  setAvailableBalance(amount: number): void {
    this.availableBalance = amount;
    const maxIdx = this.affordableDenominations().length - 1; // -1 = none affordable
    const selectionDropped = this.currentDenominationIndex > maxIdx;
    if (selectionDropped) {
      this.currentDenominationIndex = Math.max(0, maxIdx);
    }
    if (maxIdx === this.lastAffordableMaxIndex && !selectionDropped) return;
    this.lastAffordableMaxIndex = maxIdx;
    if (this.loaded) this.createVisibleBills();
  }

  private areDenominationSetsEqual(left: Denomination[], right: Denomination[]): boolean {
    if (left.length !== right.length) return false;
    for (let i = 0; i < left.length; i++) {
      if (left[i] !== right[i]) return false;
    }
    return true;
  }

  private recalculateBillPositions(): void {
    this.billRestPositions = [];
    this.billBasePositions = [];

    const selectedDenomination = this.getSelectedDenomination();

    for (let i = 0; i < this.visibleDenominations.length; i++) {
      const denomination = this.visibleDenominations[i];
      const isCurrentDenom = denomination === selectedDenomination;

      const baseY = 0.3;
      const baseZ = i * -0.02;
      const baseRotY = i * 0.02;

      const selectedTiltZ = SELECTED_BILL_TILT_Z;
      const selectedRaiseY = SELECTED_BILL_RAISE_Y;
      const selectedForwardX = SELECTED_BILL_FORWARD_X;

      this.billBasePositions.push({
        x: 0,
        y: baseY,
        z: baseZ,
        rotY: baseRotY,
        rotZ: 0,
      });

      this.billRestPositions.push({
        x: isCurrentDenom ? selectedForwardX : 0,
        y: isCurrentDenom ? baseY + selectedRaiseY : baseY,
        z: baseZ,
        rotY: baseRotY,
        rotZ: isCurrentDenom ? selectedTiltZ : 0,
      });
    }
  }

  private animateDenominationSelection(): void {
    const selectedDenomination = this.getSelectedDenomination();
    const selectedIndex = this.visibleDenominations.indexOf(selectedDenomination);

    for (let i = 0; i < this.visibleBills.length; i++) {
      const bill = this.visibleBills[i];
      const base = this.billBasePositions[i];
      if (!bill || !base) continue;

      this.applyBillHighlight(bill, false);
      bill.animateTo(
        { x: base.x, y: base.y, z: base.z },
        { x: 0, y: base.rotY, z: base.rotZ }
      );
    }

    if (selectedIndex < 0) return;

    const selectedBill = this.visibleBills[selectedIndex];
    const rest = this.billRestPositions[selectedIndex];
    if (!selectedBill || !rest) return;

    setTimeout(() => {
      selectedBill.animateTo(
        { x: rest.x ?? 0, y: rest.y, z: rest.z },
        { x: 0, y: rest.rotY, z: rest.rotZ ?? 0 }
      );
      this.applyBillHighlight(selectedBill, true);
    }, WalletAnims.selectedBillDelay);
  }

  private applyBillHighlight(bill: Bill, isActive: boolean): void {
    const mesh = bill.getMesh();
    if (mesh.material instanceof THREE.MeshStandardMaterial) {
      mesh.material.emissiveIntensity = isActive ? WalletAnims.highlightEmissive : WalletAnims.dimEmissive;
      const scale = isActive ? WalletAnims.highlightScale : 1.0;
      mesh.scale.set(scale, scale, scale);
    }
  }

  private getSelectedDenomination(): Denomination {
    const denoms = getDenominations();
    return denoms[this.currentDenominationIndex] ?? denoms[0];
  }

  /**
   * Extract a bill from the wallet (for dragging)
   * Returns a new Bill instance at the wallet position
   */
  extractBill(): Bill {
    const denomination = getDenomination(this.currentDenominationIndex);


    const bill = Bill.acquire({
      denomination,
      segments: this.billSegments,
      shadows: this.billShadows,
      textureSize: this.billTextureSize,
      assetLoader: this.assetLoader,
      position: {
        x: this.position.x,
        y: this.position.y + BILL_EXTRACT_HEIGHT_OFFSET,
        z: this.position.z + BILL_EXTRACT_Z_OFFSET,
      },
    });

    // Face camera more directly
    bill.setRotation({ x: -Math.PI / 3, y: 0, z: 0 });

    this.scene.add(bill.getMesh());
    return bill;
  }

  /**
   * Extract a bill with a specific denomination (for clicking fanned bills)
   */
  extractBillWithDenomination(denomination: number): Bill {

    const bill = Bill.acquire({
      denomination,
      segments: this.billSegments,
      shadows: this.billShadows,
      textureSize: this.billTextureSize,
      assetLoader: this.assetLoader,
      position: {
        x: this.position.x,
        y: this.position.y + BILL_EXTRACT_HEIGHT_OFFSET,
        z: this.position.z + BILL_EXTRACT_Z_OFFSET,
      },
    });

    // Face camera more directly
    bill.setRotation({ x: -Math.PI / 3, y: 0, z: 0 });

    this.scene.add(bill.getMesh());
    return bill;
  }

  /**
   * Return a bill to the wallet (animate back)
   */
  returnBill(bill: Bill): void {
    // Animate bill back to wallet position
    bill.animateTo(
      {
        x: this.position.x + BILL_RETURN_X_OFFSET,
        y: this.position.y + BILL_RETURN_Y_OFFSET,
        z: this.position.z + BILL_RETURN_Z_OFFSET,
      },
      { x: -Math.PI / 2, y: 0, z: -Math.PI / 6 }
    );

    // Remove bill after animation completes
    setTimeout(() => {
      this.scene.remove(bill.getMesh());
      bill.dispose();
    }, WalletAnims.returnDisposeDelay);
  }

  /**
   * Open wallet (animation)
   * @param onComplete - optional callback when open animation finishes
   */
  open(onComplete?: () => void): void {
    // Legacy imperative intent supersedes any in-flight async chain — the
    // chain's awaited tween is killed below (via tweenOpenProgress), its
    // awaiter released, and this bump stops it at its chainId guard.
    this.chainId++;
    const wasClosed = !this.isOpen;
    this.isOpen = true;
    // (Open whoosh is played by tweenOpenProgress so it rides the actual motion.)
    this.tweenOpenProgress(1, WalletAnims.openDuration, WalletAnims.openEase, () => {
      // Pop the tutorial label only when:
      //   1. real closed→open transition (not a redundant re-open), AND
      //   2. wallet is actually visible + fully open when this tween finishes.
      // Guard 2 matters because setPhase → updateBettingInputGate fires an
      // early wallet.open() tween while masterGroup is still hidden; a later
      // hide()+enter() sequence kills that tween, but if we ever reach this
      // callback with the wallet not visible, the label would pop prematurely
      // (seen on banker HI_LO → BANKER_SET_POT).
      if (wasClosed && this.masterGroup.visible && this.openProgress >= 0.99) {
        this.instructionsLabel?.show();
      }
      onComplete?.();
    });
  }

  /**
   * Close wallet (animation)
   * @param onComplete - optional callback when close animation finishes
   */
  close(onComplete?: () => void): void {
    // Legacy imperative intent supersedes any in-flight async chain (see open()).
    this.chainId++;
    // (Close whoosh is played by tweenOpenProgress so it rides the actual motion.)
    // Fade the tutorial label out the moment the close animation starts so it
    // disappears in lockstep with the wallet.
    this.instructionsLabel?.hide();
    this.isOpen = false;
    this.tweenOpenProgress(0, WalletAnims.closeDuration, WalletAnims.closeEase, onComplete);
  }

  /** Apply current openProgress to masterGroup position/rotation. */
  private applyOpenProgress(): void {
    if (!this.closedPosition || !this.openPosition) return;
    // The wallet body + bills cast shadows; every open/close/slide frame
    // funnels through here, so the shadow map must follow.
    renderScheduler.markShadowsDirty();
    const t = this.openProgress;
    const x = THREE.MathUtils.lerp(this.closedPosition.x, this.openPosition.x, t);
    const y = THREE.MathUtils.lerp(this.closedPosition.y, this.openPosition.y, t);
    const z = THREE.MathUtils.lerp(this.closedPosition.z, this.openPosition.z, t);
    this.masterGroup.position.set(x, y, z);
    this.position.set(x, y, z);
    this.masterGroup.rotation.set(
      THREE.MathUtils.lerp(this.closedRotationX, this.openRotationX, t),
      THREE.MathUtils.lerp(this.closedRotationY, this.openRotationY, t),
      THREE.MathUtils.lerp(this.closedRotationZ, this.openRotationZ, t)
    );
  }

  /** Apply current selectionModeProgress to bill fan-out positions. */
  private applySelectionFan(): void {
    for (let i = 0; i < this.visibleBills.length; i++) {
      const bill = this.visibleBills[i];
      const rest = this.billRestPositions[i];
      if (!rest) continue;
      bill.setPosition({
        x: rest.x ?? 0,
        y: rest.y + i * WalletAnims.selectionFanSpacing * this.selectionModeProgress,
        z: rest.z,
      });
      bill.setRotation({ x: 0, y: rest.rotY, z: 0 });
    }
  }

  /** THE single way to kill the open/close tween. Kills it AND releases the
   *  async step awaiting its onComplete (the superseded chain then stops at
   *  its chainId guard). Never kill openCloseTween directly. */
  private killOpenCloseTween(): void {
    if (this.openCloseTween) {
      this.openCloseTween.kill();
      this.openCloseTween = null;
    }
    if (this.pendingStepResolve) {
      const release = this.pendingStepResolve;
      this.pendingStepResolve = null;
      release();
    }
  }

  private tweenOpenProgress(target: number, duration: number, ease: string, onComplete?: () => void): void {
    this.killOpenCloseTween();
    // Billfold open/close whoosh, sourced from the component's single chokepoint:
    // every open/close flows through here — legacy open()/close() AND the async
    // show()/hide() path (via tweenOpenProgressAsync) — so the sound always rides
    // the actual visible motion (never before it). Guarded against no-op tweens so
    // a redundant open/close is silent.
    if (Math.abs(target - this.openProgress) > 0.001) {
      sound.play(target >= 0.5 ? SoundId.WALLET_OPEN : SoundId.WALLET_CLOSE);
    }
    this.openCloseTween = gsap.to(this, {
      openProgress: target,
      duration,
      ease,
      onUpdate: () => { this.applyOpenProgress(); },
      onComplete: () => {
        this.openCloseTween = null;
        onComplete?.();
      },
    });
  }

  setClosedTransform(
    position: { x: number; y: number; z: number },
    rotationY = 0,
    rotationX = 0,
    rotationZ = 0
  ): void {
    this.closedPosition = new THREE.Vector3(position.x, position.y, position.z);
    this.closedRotationY = rotationY;
    this.closedRotationX = rotationX;
    this.closedRotationZ = rotationZ;
  }

  setOpenTransform(
    position: { x: number; y: number; z: number },
    rotationY = WALLET_MASTER_ROTATION_Y,
    rotationX = 0,
    rotationZ = 0
  ): void {
    this.openPosition = new THREE.Vector3(position.x, position.y, position.z);
    this.openRotationY = rotationY;
    this.openRotationX = rotationX;
    this.openRotationZ = rotationZ;
  }

  /**
   * Immediately snap wallet to current target transform (no lerp).
   * Call after setClosedTransform/setOpenTransform to prevent visual flash
   * when transitioning between game phases.
   * No-op while an entry animation is playing to prevent snapping mid-slide.
   */
  syncTransformImmediate(): void {
    if (this.animatingEntry) return;
    // Kill running GSAP tween — we're snapping. Must go through
    // killOpenCloseTween: an async transition step may be awaiting this
    // tween's onComplete, and a raw kill would orphan it forever (the
    // stuck-in-betting incident class). We snap to the target the flags
    // already declare, so the released chain continues consistently.
    this.killOpenCloseTween();

    this.openProgress = this.isOpen ? 1 : 0;
    this.applyOpenProgress();
  }

  /**
   * Update visible bills (call every frame).
   * Only needed during brief bill animations (denomination selection, bill return).
   * Wallet position/rotation and selection fan-out are driven by GSAP tweens.
   */
  update(deltaTime: number): void {
    for (const bill of this.visibleBills) {
      bill.update(deltaTime);
    }
  }

  /**
   * Get current denomination
   */
  getCurrentDenomination(): number {
    return getDenomination(this.currentDenominationIndex);
  }

  /**
   * Get current denomination index
   */
  getCurrentDenominationIndex(): number {
    return this.currentDenominationIndex;
  }

  /**
   * Get wallet position
   */
  getPosition(): THREE.Vector3 {
    return this.position.clone();
  }

  setPosition(position: { x: number; y: number; z: number }): void {
    this.position.set(position.x, position.y, position.z);
    this.masterGroup.position.copy(this.position);
  }

  /**
   * Get master group (contains wallet + bills + accumulated bills)
   */
  getMasterGroup(): THREE.Group {
    return this.masterGroup;
  }

  /**
   * Get wallet group (for raycasting)
   */
  getGroup(): THREE.Group | null {
    return this.walletGroup;
  }

  /**
   * Get all visible bill meshes (for raycasting)
   */
  getVisibleBillMeshes(): THREE.Mesh[] {
    return this.visibleBills.map(bill => bill.getMesh());
  }

  /**
   * Get denomination of a specific visible bill mesh
   */
  getDenominationFromMesh(mesh: THREE.Mesh): number | null {
    for (const bill of this.visibleBills) {
      if (bill.getMesh() === mesh) {
        return bill.getDenomination();
      }
    }
    return null;
  }

  /**
   * Check if wallet is loaded
   */
  isLoaded(): boolean {
    return this.loaded;
  }

  /**
   * Check if wallet is open
   */
  getIsOpen(): boolean {
    return this.isOpen;
  }

  // Overload: legacy 0-arg show() is an instant masterGroup visibility flip
  // used by BettingController internals (onIdleEnter etc.) — kept until the
  // step-6 cleanup. New options form is the async target-state API.
  show(): void;
  show(opts: { open: boolean; animated?: boolean }): Promise<void>;
  show(opts?: { open: boolean; animated?: boolean }): Promise<void> | void {
    if (!opts) {
      if (this.animatingEntry) return;
      this.masterGroup.visible = true;
      return;
    }
    return this.transitionTo(opts.open ? 'open' : 'closed', opts.animated ?? true);
  }

  // Overload: legacy 0-arg hide() is an instant teardown. New options form
  // returns a Promise that resolves once the wallet has fully settled into
  // the hidden state.
  hide(): void;
  hide(opts: { animated?: boolean }): Promise<void>;
  hide(opts?: { animated?: boolean }): Promise<void> | void {
    if (!opts) {
      // Instant teardown supersedes any in-flight async chain: bump so the
      // released chain stops at its guard, then kill-and-release its awaiter.
      this.chainId++;
      this.killOpenCloseTween();
      if (this.selectionTween) { this.selectionTween.kill(); this.selectionTween = null; }
      this.masterGroup.visible = false;
      this.animatingEntry = false;
      this.isOpen = false;
      // Snap fully closed, silently. Instant teardown must not leave
      // openProgress stranded mid-open (the killed tween never ticks), and it
      // must not route through close()/tweenOpenProgress — that plays the
      // close whoosh for a motion nobody sees (heard when leaving a game).
      this.openProgress = 0;
      this.applyOpenProgress();
      this.targetState = 'hidden';
      // Phase-exit hide is instant — label hides instantly too, in lockstep.
      this.instructionsLabel?.hide();
      return;
    }
    return this.transitionTo('hidden', opts.animated ?? true);
  }

  /** True while a transitionTo animation chain is running. */
  isAnimating(): boolean {
    return this.inflightTransition !== null;
  }

  /** True while the wallet is animating IN (slide-in / open toward a visible
   *  state). False on the way out (target 'hidden') or when settled. */
  isAnimatingIn(): boolean {
    return this.inflightTransition !== null && this.targetState !== 'hidden';
  }

  /**
   * Fast-forward an in-flight slide-in / open entrance to its settled pose
   * immediately. Used when the player taps the wallet while it's still
   * animating in — the tap snaps the entrance so the note pull feels instant.
   * No-op when nothing is in flight or when the transition is a hide: we only
   * skip the IN animation, never the way out.
   */
  completeEntryNow(): void {
    if (!this.inflightTransition) return;
    if (this.targetState === 'hidden') return;
    this.entryTweenSkip = true;
    // GSAP fires onComplete synchronously on progress(1), resolving the awaited
    // step; runTransition then advances to the next step, which sees
    // entryTweenSkip and finishes itself too — collapsing the whole chain now.
    if (this.openCloseTween) this.openCloseTween.progress(1);
  }

  /** Current target state (the latest declared intent, regardless of whether
   *  the animation has finished). Useful for callers that need to predict
   *  what the next idempotent show/hide would do. */
  getTargetState(): WalletState {
    return this.targetState;
  }

  /**
   * Idempotent target-state transition.
   *
   * If the wallet is already settled at `target` and no animation is in
   * flight, resolves immediately with no animation. Otherwise computes the
   * minimum chain of slide-in / open / close steps needed to reach the
   * target from the current state and resolves once the chain finishes.
   *
   * If a transition is already in flight, the previous target is dropped:
   * the new target wins, the in-flight tween is killed, and the OLD promise
   * is resolved (not rejected) so any awaiter is released cleanly.
   */
  private async transitionTo(target: WalletState, animated: boolean): Promise<void> {
    // Idempotent: same target and already at rest → no-op.
    if (this.targetState === target && !this.inflightTransition) {
      return;
    }

    this.targetState = target;
    // Fresh transition starts un-skipped; completeEntryNow() may set it true
    // mid-chain, and the chain consumes it before the next transition begins.
    this.entryTweenSkip = false;

    // This chain is the new owner. Kill any in-flight tween AND release its
    // awaiter (killOpenCloseTween) — the superseded chain wakes, sees a newer
    // chainId at its guard, and stops. This is what the old comment promised
    // ("the old promise is resolved, not rejected") but never implemented:
    // before this, a superseding transitionTo left the previous awaiter
    // suspended forever.
    this.chainId++;
    const chain = this.chainId;
    this.killOpenCloseTween();
    this.animatingEntry = false;

    if (!animated) {
      this.snapToState(target);
      this.inflightTransition = null;
      return;
    }

    const transition = this.runTransition(target, chain);
    this.inflightTransition = transition;
    try {
      await transition;
    } finally {
      // Only clear if this transition is still the active one. A new
      // transitionTo call would have already replaced inflightTransition
      // and we mustn't stomp on it.
      if (this.inflightTransition === transition) {
        this.inflightTransition = null;
      }
    }
  }

  /** Snap the wallet to a target state with no animation. Used by the
   *  animated:false path (late-join / state:full restore). */
  private snapToState(target: WalletState): void {
    switch (target) {
      case 'hidden':
        this.masterGroup.visible = false;
        this.isOpen = false;
        this.openProgress = 0;
        this.applyOpenProgress();
        this.instructionsLabel?.hide();
        break;
      case 'closed':
        this.masterGroup.visible = true;
        this.isOpen = false;
        this.openProgress = 0;
        this.applyOpenProgress();
        this.instructionsLabel?.hide();
        break;
      case 'open':
        this.masterGroup.visible = true;
        this.isOpen = true;
        this.openProgress = 1;
        this.applyOpenProgress();
        // Tutorial label only pops if the wallet was actually closed before;
        // for snap (instant) we treat it as "already visible at target",
        // so re-showing the label is appropriate.
        this.instructionsLabel?.show();
        break;
    }
  }

  /** Run the actual animation chain from the current visual state to the
   *  target. Each step waits for its tween to complete before the next
   *  starts; the public Promise resolves when the chain ends. */
  private async runTransition(target: WalletState, chain: number): Promise<void> {
    // After EVERY awaited step: if a newer chain took over while we were
    // suspended (our await was force-released by killOpenCloseTween), stop —
    // the new owner runs the remaining choreography.
    switch (target) {
      case 'hidden': {
        if (this.isOpen) {
          // Match legacy close() ordering: flip flags BEFORE the tween so
          // consumers polling isOpen mid-tween see the target state.
          this.instructionsLabel?.hide();
          this.isOpen = false;
          await this.tweenOpenProgressAsync(0, WalletAnims.closeDuration, WalletAnims.closeEase);
          if (chain !== this.chainId) return;
        }
        // No slide-out animation today — the camera typically pans away,
        // so an instant masterGroup hide is fine here. If slide-out is
        // desired later, animate openProgress back to entryStartProgress.
        this.masterGroup.visible = false;
        return;
      }

      case 'closed': {
        if (!this.masterGroup.visible) {
          await this.slideInAsync();
          // slideIn lands at openProgress 0, isOpen false — already 'closed'.
          return;
        }
        if (this.isOpen) {
          this.instructionsLabel?.hide();
          this.isOpen = false;
          await this.tweenOpenProgressAsync(0, WalletAnims.closeDuration, WalletAnims.closeEase);
        }
        return;
      }

      case 'open': {
        if (!this.masterGroup.visible) {
          await this.slideInAsync();
          if (chain !== this.chainId) return;
        }
        if (!this.isOpen) {
          this.isOpen = true;
          await this.tweenOpenProgressAsync(1, WalletAnims.openDuration, WalletAnims.openEase);
          if (chain !== this.chainId) return;
          // Tutorial-label pop guard mirrors the legacy open() callback:
          // only on a real closed→open transition where the wallet is
          // actually visible at the end of the tween.
          if (this.masterGroup.visible && this.openProgress >= 0.99) {
            this.instructionsLabel?.show();
          }
        }
        return;
      }
    }
  }

  /** Promise wrapper around tweenOpenProgress for use inside async chains. */
  private tweenOpenProgressAsync(target: number, duration: number, ease: string): Promise<void> {
    return new Promise((resolve) => {
      const release = () => {
        if (this.pendingStepResolve === release) this.pendingStepResolve = null;
        resolve();
      };
      // Start the tween FIRST (its internal killOpenCloseTween releases any
      // previous step), THEN register ourselves as the live step awaiter.
      this.tweenOpenProgress(target, duration, ease, release);
      this.pendingStepResolve = release;
      // A skip requested earlier in the chain finishes this freshly-started
      // step at once (progress(1) fires onComplete → release, synchronously).
      if (this.entryTweenSkip && this.openCloseTween) this.openCloseTween.progress(1);
    });
  }

  /** Promise wrapper around the slide-in entry animation. */
  private slideInAsync(): Promise<void> {
    return new Promise((resolve) => {
      const release = () => {
        if (this.pendingStepResolve === release) this.pendingStepResolve = null;
        this.animatingEntry = false;
        resolve();
      };
      // (Open whoosh is played once at the top of runTransition for the whole
      // slide-in/open transition — not here, or entry would double up.)
      // Mirrors the legacy enter() body but resolves a Promise instead of
      // firing a callback. Kept inline (rather than calling enter()) so the
      // legacy bridge can be removed in step 6 without dragging this with it.
      this.killOpenCloseTween();
      this.openProgress = WalletAnims.entryStartProgress;
      this.isOpen = false;
      this.applyOpenProgress();
      this.masterGroup.visible = true;
      this.animatingEntry = true;
      this.openCloseTween = gsap.to(this, {
        openProgress: 0,
        duration: WalletAnims.entryDuration,
        ease: WalletAnims.entryEase,
        onUpdate: () => { this.applyOpenProgress(); },
        onComplete: () => {
          this.openCloseTween = null;
          release();
        },
      });
      this.pendingStepResolve = release;
      // If a tap requested an instant entrance before the slide started its
      // own step, finish it now so the chain collapses straight to settled.
      if (this.entryTweenSkip && this.openCloseTween) this.openCloseTween.progress(1);
    });
  }

  /**
   * Dispose of resources
   */
  dispose(): void {
    // Kill active tweens — releasing any suspended transition awaiter so a
    // teardown mid-animation can't leave a hung await upstream.
    this.chainId++;
    this.killOpenCloseTween();
    if (this.selectionTween) { this.selectionTween.kill(); this.selectionTween = null; }
    // Remove visible bills
    for (const bill of this.visibleBills) {
      this.visibleBillsGroup.remove(bill.getMesh());
      bill.dispose();
    }
    this.visibleBills = [];

    // Remove master group (contains everything)
    this.scene.remove(this.masterGroup);

    // Dispose wallet meshes
    if (this.walletGroup) {
      this.walletGroup.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry.dispose();
          if (Array.isArray(obj.material)) {
            obj.material.forEach((mat) => mat.dispose());
          } else {
            obj.material.dispose();
          }
        }
      });
      this.walletGroup = null;
    }

    // Remove the DOM instruction label ("HOLD TO SWAP") — it lives on the
    // ui-container, not the 3D scene, so it leaks onto the next screen if not
    // explicitly destroyed.
    this.instructionsLabel?.destroy();
    this.instructionsLabel = null;

    this.loaded = false;
  }
}
